import type { FC } from 'react';
import React, { useMemo } from 'react';
import type { WebsiteHeaderLayout_PhotoBackground, WebsitePhotoCropInfo } from '@shared/types/WebsiteHeaderSettings.types';
import { Box, Flex } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { LazyScreen } from '@apps/guest/packages/layout-engine/widgets/Custom/components/LazyScreen/LazyScreen';
import { AnimatedImageBox } from '@apps/guest/packages/layout-engine/widgets/Custom/components/PhotoTrio/PhotoTrio.styles';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { BrannanNavigation, EventPageType } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { GuestSiteEventDesign } from '../../../../layout.types';
import { CroppedProgressiveImage, ProgressiveImage } from '../components/ProgressiveImage';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useTranslation } from '@shared/core';
import type { HorizontalAlignment } from '@shared/types/WebsiteHeaderSettings.types';
import { isInIframe } from '@shared/utils/isInIframe';

export type PhotoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_PhotoBackground;
    showSkeleton?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
    placeholderImageUrl?: string;
    onTextAlignmentChange?: (alignment: HorizontalAlignment) => void;
  } & EventNameNavForStandaloneHeaderProps
>;

type PhotoBackgroundRendererProps = Readonly<{
  photo?: Photo;
  placeholderImageUrl?: string;
  height: string;
  containerDimensions: Readonly<{
    width: number;
    height: number;
  }>;
  isCompactView: boolean;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
  isMobile: boolean;
  deviceSpecificContainerAspectRatio: number;
  currentPageType: EventPageType | undefined;
  pageSlug: string | undefined;
  handleEditPhotoClick: () => void;
}>;

type Photo = Readonly<{
  id: string;
  url: string;
  cropInfo?: WebsitePhotoCropInfo;
  mobileCropInfo?: WebsitePhotoCropInfo;
}>;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;
const CONTAINER_MOBILE_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.mobileAspectRatio as number;

const isPreviewing = isInIframe();

const PhotoBackgroundRenderer: FC<PhotoBackgroundRendererProps> = props => {
  const {
    photo,
    placeholderImageUrl,
    containerDimensions,
    height,
    isCompactView,
    renderedContainerHeight,
    renderedContainerWidth,
    isMobile,
    deviceSpecificContainerAspectRatio,
    currentPageType,
    pageSlug,
    handleEditPhotoClick,
    children,
  } = props;

  const { tq } = useTranslation('inlineEditor');

  const containerWidth = renderedContainerWidth ?? containerDimensions.width;
  const containerHeight = renderedContainerHeight ?? containerDimensions.height;

  const derivedWidth = containerHeight * deviceSpecificContainerAspectRatio;
  const derivedHeight = containerWidth / deviceSpecificContainerAspectRatio;

  const { bestSuitableWidth, bestSuitableHeight } =
    derivedWidth < containerWidth
      ? { bestSuitableWidth: containerWidth, bestSuitableHeight: derivedHeight }
      : { bestSuitableWidth: derivedWidth, bestSuitableHeight: containerHeight };

  const cropInfoToUse = isMobile && photo?.mobileCropInfo ? photo.mobileCropInfo : photo?.cropInfo;

  const inlineEditorProps = {
    elementLabel: tq`Photo`,
    wrapperType: 'actionInside' as const,
    actionData: { editPhoto: handleEditPhotoClick },
    componentName: 'headerPhoto' as const,
    pageName: currentPageType,
    pageSlug: pageSlug || '',
    wrapperCSS: { width: '100%', height: '100%' },
    isEligibleForInlineEditing: !isCompactView,
  };

  const childrenOverlay = (
    <Box
      width="100%"
      height="100%"
      position={isCompactView ? 'relative' : 'absolute'}
      zIndex="banner"
      pointerEvents={isPreviewing ? 'none' : 'auto'}
    >
      {children}
    </Box>
  );

  if (cropInfoToUse && photo) {
    return (
      <AnimatedImageBox
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems={isCompactView ? 'flex-start' : 'center'}
        backgroundColor="lightgray"
      >
        <InlineEditor {...inlineEditorProps}>
          <CroppedProgressiveImage
            width={isCompactView ? containerWidth : bestSuitableWidth}
            height={isCompactView ? derivedHeight : bestSuitableHeight}
            photoUrl={photo.url}
            cropInfo={{ ...cropInfoToUse, unit: '%' }}
            containerStyles={{ position: 'absolute' }}
          />
        </InlineEditor>
        {childrenOverlay}
      </AnimatedImageBox>
    );
  }

  const backgroundImageUrl = photo?.url || placeholderImageUrl;

  return (
    <AnimatedImageBox
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${deviceSpecificContainerAspectRatio}`}
      overflow="hidden"
      backgroundColor="lightgray"
      minHeight={pxToRem(96)}
    >
      {backgroundImageUrl && (
        <InlineEditor {...inlineEditorProps}>
          <ProgressiveImage
            photoUrl={backgroundImageUrl}
            width="100%"
            height="100%"
            containerStyles={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </InlineEditor>
      )}
      {childrenOverlay}
    </AnimatedImageBox>
  );
};

export const PhotoBackgroundLayout: FC<PhotoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    placeholderImageUrl,
    onTextAlignmentChange,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];
  const { headerTitleStyle, navigationStyle } = websiteHeader.headerPhoto;

  const [containerDimensions, containerRef] = useSizeObserver();

  const [isMobile = false] = useResponsive({ values: { xxs: true, sm: false } });
  const [isBouncingChevronEnabled] = useIsFeatureEnabled('guestBackgroundHeaderBouncingChevronEnabled');
  const deviceSpecificContainerAspectRatio = isMobile && photo?.mobileCropInfo ? CONTAINER_MOBILE_ASPECT_RATIO : CONTAINER_ASPECT_RATIO;

  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  // compact view is only for brannan classic navigation layout
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && currentPageType !== EventPageType.welcome && pageIndex !== 0;

  //  Hide photo background in compact view when navbar is in menu variant
  // The photobackground is not needed for the menu variant as it is a hamburger menu
  // The hamburger menu is shown only in mobile devices
  const shouldHidePhotoBackground = useMemo(() => {
    return isCompactView && restProps.navBarVariant === 'menu';
  }, [isCompactView, restProps.navBarVariant]);

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : isMobile && isBouncingChevronEnabled ? '90svh' : '100svh',
            aspectRatio: `${deviceSpecificContainerAspectRatio}`,
          },
    [deviceSpecificContainerAspectRatio, isBouncingChevronEnabled, isCompactView, isInlineEditing, isMobile, renderedContainerHeight],
  );

  const viewportHeightFallback = isMobile && isBouncingChevronEnabled ? '90svh' : '100svh';

  // Memoize container dimensions to prevent unnecessary style recalculations
  // when hiding photo background on mobile/tablet
  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHidePhotoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHidePhotoBackground ? 'auto' : height,
      containerMinHeight: shouldHidePhotoBackground ? 'auto' : pxToRem(96),
      // Allow overflow when inline editing so the alignment toolbar isn't clipped by the container
      containerOverflow: shouldHidePhotoBackground || isInlineEditing ? 'visible' : 'hidden',
    };
  }, [shouldHidePhotoBackground, aspectRatio, height, isInlineEditing]);

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const handleEditPhotoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  });

  return (
    <Box
      position="relative"
      width="100%"
      zIndex="docked"
    >
      <Flex
        ref={containerRef}
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHidePhotoBackground ? (
          <EventNameNavForStandaloneHeader
            eventDisplayName={websiteHeader.eventDisplayName}
            colors={websiteHeader.headerPhoto.colors}
            headerTextData={websiteHeader.headerPhoto.eventName}
            headerTitleStyle={headerTitleStyle}
            navigationStyle={navigationStyle}
            isCompactView={isCompactView}
            currentPageType={currentPageType}
            handleEditTextClick={handleEditTextClick}
            isContentEditable={isInlineEditing}
            editableContentRef={editableContentRef}
            forceRenderViewIn={forceRenderViewIn}
            scaleValue={scaleValue}
            pages={pages}
            pageSlug={pageSlug}
            renderedContainerHeight={renderedContainerHeight}
            renderedContainerWidth={renderedContainerWidth}
            viewportHeightFallback={viewportHeightFallback}
            onTextAlignmentChange={onTextAlignmentChange}
            {...restProps}
          />
        ) : (
          <LazyScreen
            src={addRendition({ url: photo?.url || placeholderImageUrl, renditionSize: 'medium' })}
            width="100%"
            height={height}
            aspectRatio={aspectRatio}
            skeletonGroupOverrides={{
              Root: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                },
              },
              PlaceholderRoot: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                  position: 'absolute',
                },
              },
            }}
            showSkeleton={showSkeleton}
          >
            {() => (
              <PhotoBackgroundRenderer
                photo={photo}
                placeholderImageUrl={placeholderImageUrl}
                containerDimensions={containerDimensions}
                height={height}
                isCompactView={isCompactView}
                renderedContainerHeight={renderedContainerHeight}
                renderedContainerWidth={renderedContainerWidth}
                isMobile={isMobile}
                deviceSpecificContainerAspectRatio={deviceSpecificContainerAspectRatio}
                currentPageType={currentPageType}
                pageSlug={pageSlug}
                handleEditPhotoClick={handleEditPhotoClick}
              >
                <EventNameNavForStandaloneHeader
                  eventDisplayName={websiteHeader.eventDisplayName}
                  colors={websiteHeader.headerPhoto.colors}
                  headerTextData={websiteHeader.headerPhoto.eventName}
                  headerTitleStyle={headerTitleStyle}
                  navigationStyle={navigationStyle}
                  isCompactView={isCompactView}
                  currentPageType={currentPageType}
                  handleEditTextClick={handleEditTextClick}
                  isContentEditable={isInlineEditing}
                  editableContentRef={editableContentRef}
                  forceRenderViewIn={forceRenderViewIn}
                  scaleValue={scaleValue}
                  pages={pages}
                  pageSlug={pageSlug}
                  renderedContainerHeight={renderedContainerHeight}
                  renderedContainerWidth={renderedContainerWidth}
                  viewportHeightFallback={viewportHeightFallback}
                  onTextAlignmentChange={onTextAlignmentChange}
                  {...restProps}
                />
              </PhotoBackgroundRenderer>
            )}
          </LazyScreen>
        )}
      </Flex>
      {isBouncingChevronEnabled && isMobile && !isCompactView && !shouldHidePhotoBackground && !isInlineEditing && <BouncingChevron />}
    </Box>
  );
};

PhotoBackgroundLayout.displayName = 'PhotoBackgroundLayout';
